import { useCallback, useEffect, useMemo } from 'react';
import { useGetTrafficByType, TrafficRecord } from './useGetTrafficByType';
import { useGetTrafficByTypeAndBrands } from './useGetTrafficByTypeAndBrands';
import { useBrandTrafficStore } from '../../../zustandStores/BrandTrafficStore';
import { useAppSelector } from 'src/utils/Hooks';
import {
  groupKeywordDataByBrand,
  groupEntityDataByBrand,
  EntityRecord,
  EntityBrandRecord
} from '../utils/brandDataUtils';

export interface UseUnifiedTrafficDataProps {
  retailerId?: number;
  categoryIds?: number[];
  enabled?: boolean;
  keywordLimit?: number;
  entityLimit?: number;
  apiMetricFieldName?: string;
  selectedGroupByField: {
    id: string;
    label: string;
  };
  selectedTimePeriod?: 'current' | 'comparison';
  selectedBrands?: number[];
}

/**
 * Utility function to convert TrafficRecord[] to EntityBrandRecord[] for use with groupEntityDataByBrand
 * Ensures the required brandId property is present
 */
const convertToEntityBrandRecords = (records: TrafficRecord[]): EntityBrandRecord[] => {
  return records
    .filter((record) => record.brandId !== undefined)
    .map((record) => ({
      fieldKey: record.fieldKey,
      brandId: record.brandId as number,
      value: record.value
    }));
};

/**
 * A unified hook that handles all traffic data fetching scenarios:
 * 1. Brand traffic data (for store initialization)
 * 2. Entity traffic data (keywords, categories, subcategories, topics)
 * 3. Combined brand and entity data
 * 4. Comparison period data
 *
 * This replaces both useTrafficData and useBrandTrafficData hooks
 */
export const useUnifiedTrafficData = ({
  retailerId,
  categoryIds,
  enabled = true,
  keywordLimit = 500,
  entityLimit = 20,
  apiMetricFieldName = 'totalClicks',
  selectedGroupByField,
  selectedTimePeriod = 'current',
  selectedBrands = []
}: UseUnifiedTrafficDataProps) => {
  const comparisonTimePeriod = useAppSelector((state) => state.comparisonTimePeriod);

  // Get access to store methods and state
  const {
    setBrands,
    setKeywordBrandTraffic,
    setIsLoadingBrands,
    setIsLoadingKeywordBrands,
    setError,
    brands,
    keywordBrandTraffic,
    getBrandColor,
    getSelectedBrands,
    getTopBrands,
    userChangedBrands,
    pickTop4BrandsByVolume
  } = useBrandTrafficStore();

  // 1. Fetch brand traffic data (always needed for store initialization) -> need to find top 4 brands
  const {
    data: brandTrafficData,
    isLoading: isLoadingBrandTraffic,
    error: brandTrafficError
  } = useGetTrafficByType({
    metricType: apiMetricFieldName,
    fieldName: 'brand',
    queryKey: 'brand-traffic',
    categoryIds,
    resultCount: 500,
    enabled
  });

  // 2. Fetch entity traffic data for current period -> need to find total entity traffic by each entity (keywords, categories, subcategories)
  const { data: entityTrafficData, isLoading: isLoadingEntityTraffic } = useGetTrafficByType({
    metricType: apiMetricFieldName,
    fieldName: selectedGroupByField.id,
    queryKey: `${selectedGroupByField.id}-traffic`,
    categoryIds,
    resultCount: 100,
    additionalParams: {
      ...(selectedGroupByField.id === 'keyword' && {
        excludeKeywords: ['[other traffic]', '[Other traffic]']
      })
    },
    enabled
  });

  // Extract keywords/entities from current period for consistent comparison fetching
  const currentEntityKeys = useMemo(() => {
    if (selectedGroupByField.id === 'keyword' && entityTrafficData?.records) {
      return entityTrafficData.records.map((record) => String(record.fieldKey));
    }
    return [];
  }, [selectedGroupByField.id, entityTrafficData]);

  // 3. Fetch comparison period entity data
  const { data: comparisonEntityTrafficData, isLoading: isLoadingComparisonEntityTraffic } = useGetTrafficByType({
    metricType: apiMetricFieldName,
    fieldName: selectedGroupByField.id,
    queryKey: `${selectedGroupByField.id}-traffic-comparison`,
    categoryIds,
    resultCount: 100,
    additionalParams: {
      ...(retailerId ? { retailerId: +retailerId } : {}),
      ...(selectedGroupByField.id === 'keyword' && {
        excludeKeywords: ['[other traffic]', '[Other traffic]'],
        ...(currentEntityKeys.length > 0 && { keywords: currentEntityKeys })
      }),
      startWeekId: comparisonTimePeriod?.startWeek,
      endWeekId: comparisonTimePeriod?.endWeek
    },
    enabled:
      enabled &&
      (selectedTimePeriod === 'comparison' || selectedGroupByField.id !== 'keyword' || currentEntityKeys.length > 0)
  });

  // Prepare payload for the brand + entity API call
  const getPayloadForGroupingType = useCallback(
    (entities: EntityRecord[]) => {
      if (selectedGroupByField.id === 'keyword') {
        return { keywords: entities.map((k) => String(k.fieldKey)) };
      } else if (selectedGroupByField.id === 'category' && entities.length > 0) {
        return { categoryIds: entities.slice(0, entityLimit).map((e) => Number(e.fieldKey)) };
      } else if (selectedGroupByField.id === 'subcategory' && entities.length > 0) {
        return { subcategoryIds: entities.slice(0, entityLimit).map((e) => Number(e.fieldKey)) };
      } else if (selectedGroupByField.id === 'topic' && entities.length > 0) {
        return { topicIds: entities.slice(0, entityLimit).map((e) => String(e.fieldKey)) };
      }
      return {};
    },
    [selectedGroupByField.id, entityLimit]
  );

  // Convert entity traffic data to EntityRecord format
  const entities = useMemo<EntityRecord[]>(() => {
    if (!entityTrafficData?.records?.length) return [];
    return entityTrafficData.records.map((record) => ({
      fieldKey: record.fieldKey,
      value: record.value as number,
      displayName: String(record.fieldKey)
    }));
  }, [entityTrafficData]);

  // 4. Fetch combined brand and entity data for current period using the payload for the grouping type
  const { data: brandEntityTrafficData, isLoading: isLoadingBrandEntityTraffic } = useGetTrafficByTypeAndBrands({
    metricType: apiMetricFieldName,
    fieldName: `${selectedGroupByField.id}AndBrand`,
    brandIds: selectedBrands,
    categoryIds,
    ...getPayloadForGroupingType(entities),
    resultCount: 400,
    enabled: enabled && entities.length > 0 && selectedBrands.length > 0 && selectedTimePeriod === 'current'
  });

  // 5. Fetch comparison period brand entity data
  const { data: comparisonBrandEntityTrafficData, isLoading: isLoadingComparisonBrandEntityTraffic } =
    useGetTrafficByTypeAndBrands({
      metricType: apiMetricFieldName,
      fieldName: `${selectedGroupByField.id}AndBrand`,
      brandIds: selectedBrands,
      categoryIds,
      ...getPayloadForGroupingType(entities),
      resultCount: 400,
      additionalParams: {
        startWeekId: comparisonTimePeriod?.startWeek,
        endWeekId: comparisonTimePeriod?.endWeek
      },
      enabled:
        enabled &&
        entities.length > 0 &&
        selectedBrands.length > 0 &&
        selectedTimePeriod === 'comparison' &&
        !!comparisonTimePeriod
    });

  // Create traffic maps for each period
  const totalEntityTrafficMap = useMemo(() => {
    if (!entityTrafficData?.records?.length) return new Map<string, number>();
    const trafficMap = new Map<string, number>();
    entityTrafficData.records.forEach((record) => {
      trafficMap.set(String(record.fieldKey), record.value as number);
    });
    return trafficMap;
  }, [entityTrafficData]);

  const totalComparisonEntityTrafficMap = useMemo(() => {
    if (!comparisonEntityTrafficData?.records?.length) return new Map<string, number>();
    const trafficMap = new Map<string, number>();
    comparisonEntityTrafficData.records.forEach((record) => {
      trafficMap.set(String(record.fieldKey), record.value as number);
    });

    // Ensure all current period entity keys are represented in the map
    if (entityTrafficData?.records) {
      entityTrafficData.records.forEach((record) => {
        const key = String(record.fieldKey);
        if (!trafficMap.has(key)) {
          trafficMap.set(key, 0);
        }
      });
    }

    return trafficMap;
  }, [comparisonEntityTrafficData, entityTrafficData]);

  // Create comparison entities
  const comparisonEntities = useMemo<EntityRecord[]>(() => {
    if (!comparisonEntityTrafficData?.records?.length) return [];
    return comparisonEntityTrafficData.records.map((record) => ({
      fieldKey: record.fieldKey,
      value: record.value as number,
      displayName: String(record.fieldKey)
    }));
  }, [comparisonEntityTrafficData]);

  // Group data by brand
  const groupedBrandData = useMemo(() => {
    const brandEntityDataToUse =
      selectedTimePeriod === 'comparison' ? comparisonBrandEntityTrafficData : brandEntityTrafficData;

    if (brands.length === 0 || !brandEntityDataToUse?.records || entities.length === 0) {
      return [];
    }

    const brandEntityRecords = convertToEntityBrandRecords(brandEntityDataToUse.records || []);
    return groupEntityDataByBrand(brands, brandEntityRecords, entities, selectedBrands, entityLimit);
  }, [
    selectedTimePeriod,
    comparisonBrandEntityTrafficData,
    brandEntityTrafficData,
    brands,
    selectedBrands,
    entities,
    entityLimit
  ]);

  // Update store with brand traffic data
  useEffect(() => {
    setIsLoadingBrands(isLoadingBrandTraffic);

    if (brandTrafficData && !isLoadingBrandTraffic) {
      setBrands(brandTrafficData.records as any);
      pickTop4BrandsByVolume();
    }

    if (brandTrafficError) {
      setError(brandTrafficError as Error);
    }
  }, [
    brandTrafficData,
    isLoadingBrandTraffic,
    pickTop4BrandsByVolume,
    brandTrafficError,
    setBrands,
    setIsLoadingBrands,
    setError,
    userChangedBrands
  ]);

  // Update store with combined brand and keyword traffic data (only for keywords)
  useEffect(() => {
    if (selectedGroupByField.id === 'keyword') {
      setIsLoadingKeywordBrands(isLoadingBrandEntityTraffic);

      if (brandEntityTrafficData && !isLoadingBrandEntityTraffic) {
        setKeywordBrandTraffic(brandEntityTrafficData.records as any);
      }
    }
  }, [
    selectedGroupByField.id,
    brandEntityTrafficData,
    isLoadingBrandEntityTraffic,
    setKeywordBrandTraffic,
    setIsLoadingKeywordBrands
  ]);

  // Calculate loading states
  const isLoadingKeywords = selectedGroupByField.id === 'keyword' ? isLoadingEntityTraffic : false;
  const isLoadingBrands = isLoadingBrandTraffic;

  const isLoading =
    isLoadingBrandTraffic ||
    (selectedGroupByField.id !== 'keyword' && isLoadingEntityTraffic) ||
    (selectedTimePeriod === 'current' && isLoadingBrandEntityTraffic) ||
    (selectedTimePeriod === 'comparison' && isLoadingComparisonBrandEntityTraffic) ||
    isLoadingComparisonEntityTraffic;

  // Extract all keywords for backward compatibility
  const allKeywords = useMemo(() => {
    if (selectedGroupByField.id === 'keyword' && entityTrafficData?.records?.length) {
      return entityTrafficData.records.map((record) => record.fieldKey as string);
    }
    return [];
  }, [selectedGroupByField.id, entityTrafficData]);

  const topKeywords = useMemo(() => {
    return allKeywords.slice(0, keywordLimit);
  }, [allKeywords, keywordLimit]);

  const topBrands = useMemo(() => getTopBrands(5), [getTopBrands]);
  const selectedBrandRecords = useMemo(() => getSelectedBrands(), [getSelectedBrands]);

  return {
    // Original useBrandTrafficData return values
    isLoading,
    error: brandTrafficError,
    hasBrands: selectedBrands.length > 0,
    topBrands,
    selectedBrandRecords,
    brandKeywordTrafficData: brandEntityTrafficData,
    topKeywords,
    allKeywords,

    // Original useTrafficData return values
    entityTrafficData,
    comparisonEntityTrafficData,
    brandEntityTrafficData,
    comparisonBrandEntityTrafficData,
    isLoadingEntityTraffic,
    isLoadingComparisonEntityTraffic,
    isLoadingBrandEntityTraffic,
    isLoadingComparisonBrandEntityTraffic,

    // Additional processed data
    entitiesForNonBrandChart: entities,
    comparisonEntitiesForNonBrandChart: comparisonEntities,
    totalEntityTrafficMap,
    totalComparisonEntityTrafficMap,
    groupedBrandData,

    // Store methods for backward compatibility
    isLoadingKeywords,
    isLoadingBrands,
    brands,
    keywordBrandTraffic,
    getBrandColor,
    selectedBrands,
    pickTop4BrandsByVolume
  };
};
